class UgcShowcase extends HTMLElement {
  connectedCallback() {
    this.isVisible = true;
    this.handleSelect = this.handleSelect.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.resumeScrollSync = this.resumeScrollSync.bind(this);

    this.addEventListener('slideshow:select', this.handleSelect);
    this.addEventListener('click', this.handleClick);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = Boolean(entry?.isIntersecting);
        if (this.isVisible) this.hasBeenSeen = true;
        if (this.isVisible && this.pendingJumpSlide) {
          const slide = this.pendingJumpSlide;
          this.pendingJumpSlide = null;
          requestAnimationFrame(() => this.jumpToSlide(slide));
        }
        this.updateVideos();
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this);

    requestAnimationFrame(() => {
      this.scroller = this.querySelector('slideshow-slides');
      this.scroller?.addEventListener('scroll', this.handleScroll, { passive: true });

      const slides = this.querySelectorAll('.ugc-showcase__slide');
      const initialIndex = Number(this.querySelector('slideshow-component')?.getAttribute('initial-slide')) || 0;
      const firstVisibleSlide =
        slides[initialIndex] ||
        this.querySelector('.ugc-showcase__slide[aria-hidden="false"]') ||
        slides[0];
      // Off-screen carousels have no layout to measure yet, so the jump waits
      // until the section first scrolls into view.
      this.setActiveSlide(firstVisibleSlide);
      if (initialIndex > 0) {
        if (this.hasBeenSeen) {
          this.jumpToSlide(firstVisibleSlide);
        } else {
          this.pendingJumpSlide = firstVisibleSlide;
        }
      }
    });
  }

  /**
   * Opens on the first slide with enough neighbours before it to fill the
   * centering gutter, so no blank space shows on the left. Wider screens need
   * more than one. `slide` (from `initial-slide`) is the minimum.
   *
   * Horizon marks `initial-slide` as current before selecting it, so its own
   * select() treats the slide as already showing and never scrolls; the
   * scroll happens here instead and scroll snap settles it in the middle.
   */
  jumpToSlide(slide) {
    if (!this.scroller || !(slide instanceof HTMLElement)) return;
    // Leave the row alone once a visitor has moved it themselves.
    if (this.scroller.scrollLeft !== 0) return;

    const slides = Array.from(this.querySelectorAll('.ugc-showcase__slide'));
    const slideWidth = slide.getBoundingClientRect().width;
    if (!slideWidth) return;

    const styles = getComputedStyle(this.scroller);
    const gutter = parseFloat(styles.paddingInlineStart) || 0;
    const gap = parseFloat(styles.columnGap) || 0;
    const needed = Math.ceil(gutter / (slideWidth + gap));
    const target = slides[Math.min(Math.max(slides.indexOf(slide), needed), slides.length - 1)] || slide;

    const scrollerRect = this.scroller.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const offset = targetRect.left + targetRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);

    this.scroller.scrollBy({ left: offset, behavior: 'instant' });
    this.setActiveSlide(target);

    // Horizon's own index tracks the start edge and misses this jump, which
    // makes the first arrow press overshoot. Set it once now and again after
    // Horizon's scroll handler has run.
    const slideshow = this.querySelector('slideshow-component');
    const syncSlideshow = () => {
      const index = slideshow?.slides?.indexOf(target) ?? -1;
      if (index >= 0) slideshow.current = index;
    };
    syncSlideshow();
    requestAnimationFrame(() => requestAnimationFrame(syncSlideshow));
  }

  disconnectedCallback() {
    this.removeEventListener('slideshow:select', this.handleSelect);
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.scroller?.removeEventListener('scroll', this.handleScroll);
    this.scroller?.removeEventListener('scrollend', this.resumeScrollSync);
    clearTimeout(this.scrollSyncTimer);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.observer?.disconnect();
  }

  /** Whether the selected slide is kept in the middle of the row. */
  get centersActiveSlide() {
    return this.dataset.centerActiveSlide !== 'false';
  }

  /**
   * The slide sitting closest to the middle of the scroller.
   *
   * Horizon tracks the current slide against the *start* edge of the scroller
   * whenever more than one slide is visible, which reports a peeking slide
   * rather than the centered one, so centered mode takes its own reading.
   */
  findCenteredSlide() {
    const slides = Array.from(this.querySelectorAll('.ugc-showcase__slide'));
    if (!this.scroller || slides.length === 0) return null;

    const bounds = this.scroller.getBoundingClientRect();
    const reference = bounds.left + bounds.width / 2;

    let closest = null;
    let closestDistance = Infinity;

    for (const slide of slides) {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - reference);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = slide;
      }
    }

    return closest;
  }

  /**
   * Ignores scroll positions while a chosen slide animates into place, so the
   * slides it travels past don't each flash as active on the way.
   */
  pauseScrollSync() {
    this.scrollSyncPaused = true;
    clearTimeout(this.scrollSyncTimer);
    this.scroller?.addEventListener('scrollend', this.resumeScrollSync, { once: true });
    this.scrollSyncTimer = setTimeout(this.resumeScrollSync, 1000);
  }

  resumeScrollSync() {
    clearTimeout(this.scrollSyncTimer);
    this.scroller?.removeEventListener('scrollend', this.resumeScrollSync);
    this.scrollSyncPaused = false;
  }

  handleScroll() {
    if (!this.centersActiveSlide || this.scrollSyncPaused || this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.setActiveSlide(this.findCenteredSlide());
    });
  }

  handleSelect(event) {
    if (!this.contains(event.target)) return;

    // A chosen slide is still scrolling into place here, so the event names the
    // destination more reliably than the DOM does. Scrolling and dragging have
    // already settled, and for those the measured slide is the correct one.
    if (event.detail?.trigger === 'select') {
      this.pauseScrollSync();
      this.setActiveSlide(event.detail.slide);
      return;
    }

    if (this.centersActiveSlide) {
      this.setActiveSlide(this.findCenteredSlide() || event.detail?.slide);
      return;
    }

    this.setActiveSlide(event.detail?.slide);
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const navButton = event.target.closest('[data-ugc-showcase-nav]');
    if (navButton && this.contains(navButton)) {
      this.handleNavigation(event, navButton.dataset.ugcShowcaseNav);
      return;
    }

    const button = event.target.closest('.ugc-showcase__sound-toggle');
    if (!button || !this.contains(button)) return;

    const soundOn = this.dataset.sound !== 'on';
    this.dataset.sound = soundOn ? 'on' : 'off';
    this.updateSoundButtons();
    this.updateVideos();
  }

  handleNavigation(event, direction) {
    event.preventDefault();

    if (direction !== 'previous' && direction !== 'next') return;

    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide')).filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );

    if (slides.length <= 1) return;

    let current = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (current < 0) current = Number(slideshow.current);

    if (!Number.isInteger(current) || current < 0 || current >= slides.length) {
      current = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
    }

    if (current < 0) current = 0;

    const offset = direction === 'previous' ? -1 : 1;
    const nextIndex = (current + offset + slides.length) % slides.length;
    this.pauseScrollSync();
    slideshow.select(nextIndex, event);
    this.setActiveSlide(slides[nextIndex]);
  }

  handleVisibilityChange() {
    this.updateVideos();
  }

  setActiveSlide(slide) {
    if (!(slide instanceof HTMLElement)) return;
    if (slide === this.activeSlide) return;

    this.activeSlide = slide;

    this.querySelectorAll('.ugc-showcase__slide').forEach((item) => {
      const isActive = item === slide;
      item.classList.toggle('is-active', isActive);
      item
        .querySelectorAll('.ugc-showcase__product-card a, .ugc-showcase__product-card button, .ugc-showcase__sound-toggle')
        .forEach((control) => {
          if (isActive) {
            control.removeAttribute('tabindex');
          } else {
            control.setAttribute('tabindex', '-1');
          }
        });
    });

    slide.classList.add('is-active');
    this.updateSoundButtons();
    this.updateVideos();
  }

  updateSoundButtons() {
    const soundOn = this.dataset.sound === 'on';
    this.querySelectorAll('.ugc-showcase__sound-toggle').forEach((button) => {
      button.setAttribute('aria-pressed', String(soundOn));
    });
  }

  /**
   * How many slides either side of the active one keep their video loaded.
   * Phones show the active slide plus a peek of each neighbour; wider screens
   * show more of the row.
   */
  get loadedVideoRadius() {
    return window.matchMedia('(min-width: 750px)').matches ? 2 : 1;
  }

  /**
   * Keeps only the videos near the active slide attached to their files.
   *
   * Every attached video holds a decoder, and iOS Safari kills the tab once a
   * dozen full-size clips are attached at the same time. Far videos hand their
   * `src` back to `data-src` and reload empty, which releases that memory.
   */
  syncVideoSources() {
    const slides = Array.from(this.querySelectorAll('.ugc-showcase__slide'));
    const activeIndex = slides.indexOf(this.activeSlide);
    const radius = this.loadedVideoRadius;

    slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      if (!video) return;

      const offset = Math.abs(index - Math.max(activeIndex, 0));
      // The row loops, so the last slides sit next to the first ones.
      const distance = Math.min(offset, slides.length - offset);
      const shouldLoad = this.hasBeenSeen && distance <= radius;
      const sources = video.querySelectorAll('source');

      if (shouldLoad) {
        let attached = false;
        sources.forEach((source) => {
          if (source.dataset.src && !source.getAttribute('src')) {
            source.setAttribute('src', source.dataset.src);
            attached = true;
          }
        });
        video.preload = 'metadata';
        if (attached) video.load();
        return;
      }

      let detached = false;
      sources.forEach((source) => {
        const src = source.getAttribute('src');
        if (!src) return;
        source.dataset.src = src;
        source.removeAttribute('src');
        detached = true;
      });
      video.preload = 'none';
      if (detached) {
        video.pause();
        video.load();
      }
    });
  }

  updateVideos() {
    const soundOn = this.dataset.sound === 'on';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.syncVideoSources();

    this.querySelectorAll('video').forEach((video) => {
      const slide = video.closest('.ugc-showcase__slide');
      const isActive = slide?.classList.contains('is-active');

      video.muted = !isActive || !soundOn;
      video.playsInline = true;
      video.removeAttribute('autoplay');
      video.autoplay = false;

      if (isActive && this.isVisible && !document.hidden && !reduceMotion) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }
}

if (!customElements.get('ugc-showcase')) {
  customElements.define('ugc-showcase', UgcShowcase);
}
